/**
 * ドリルダウン階層マスタの管理(管理者のみ)。
 * 事業部ごとに深さ・名称を自由に定義する。取引は末端に紐づけるのが基本。
 */
import { useCallback, useEffect, useState } from 'react';
import { useMasters } from './MastersProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import {
  childrenOf,
  createHierarchyNode,
  deleteHierarchyNode,
  fetchHierarchyNodes,
  updateHierarchyNode,
} from './hierarchyRepo';
import type { DepartmentId, HierarchyNode } from '@core/lib/types';

type EditState = {
  id: string | null;
  parentId: string | null;
  parentName: string;
  name: string;
  levelLabel: string;
  displayOrder: number;
  active: boolean;
};

export default function HierarchyTab() {
  const { departments } = useMasters();
  const toast = useToast();
  const [departmentId, setDepartmentId] = useState<DepartmentId>(
    (departments[0]?.id ?? 'unso') as DepartmentId,
  );
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setNodes(await fetchHierarchyNodes(departmentId, true));
    } catch (e) {
      toast(e instanceof Error ? e.message : '階層を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [departmentId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function startAdd(parent: HierarchyNode | null) {
    const siblings = childrenOf(nodes, parent?.id ?? null);
    setEdit({
      id: null,
      parentId: parent?.id ?? null,
      parentName: parent?.name ?? '(ルート)',
      name: '',
      levelLabel: parent ? '' : '拠点',
      displayOrder: (siblings.length + 1) * 10,
      active: true,
    });
  }

  function startEdit(node: HierarchyNode) {
    const parent = node.parent_id ? nodes.find((n) => n.id === node.parent_id) : null;
    setEdit({
      id: node.id,
      parentId: node.parent_id,
      parentName: parent?.name ?? '(ルート)',
      name: node.name,
      levelLabel: node.level_label,
      displayOrder: node.display_order,
      active: node.active,
    });
  }

  async function save() {
    if (!edit) return;
    setBusy(true);
    try {
      if (edit.id) {
        await updateHierarchyNode(edit.id, {
          name: edit.name,
          levelLabel: edit.levelLabel,
          displayOrder: edit.displayOrder,
          active: edit.active,
        });
        toast('階層を更新しました');
      } else {
        await createHierarchyNode(
          {
            departmentId,
            parentId: edit.parentId,
            name: edit.name,
            levelLabel: edit.levelLabel,
            displayOrder: edit.displayOrder,
            active: edit.active,
          },
          nodes,
        );
        toast('階層を追加しました');
      }
      setEdit(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function remove(node: HierarchyNode) {
    setBusy(true);
    try {
      await deleteHierarchyNode(node.id, nodes);
      toast('階層を削除しました');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '削除に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  function renderTree(parentId: string | null) {
    const children = childrenOf(nodes, parentId);
    if (children.length === 0) return null;
    return (
      <ul className="tree">
        {children.map((node) => (
          <li key={node.id}>
            <span className="lvl">{node.level_label || '—'}</span>
            <span style={{ opacity: node.active ? 1 : 0.45 }}>{node.name}</span>
            {!node.active && <span className="badge b-ng" style={{ marginLeft: 6 }}>無効</span>}
            <button className="txn-link" onClick={() => startAdd(node)}>
              下位を追加
            </button>
            <button className="txn-link" onClick={() => startEdit(node)}>
              編集
            </button>
            <button className="txn-link" onClick={() => void remove(node)} disabled={busy}>
              削除
            </button>
            {renderTree(node.id)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          ドリルダウン階層マスタ
          <select
            style={{
              marginLeft: 'auto',
              padding: '6px 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 13,
            }}
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value as DepartmentId)}
          >
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <button className="btn-line" onClick={() => startAdd(null)}>
            最上位を追加
          </button>
        </h3>
        <p className="hint" style={{ marginBottom: 12 }}>
          階層の深さ・名称は事業部ごとに自由に決められます(例: 拠点 → 事業所 → コース → 人)。
          取引は末端に紐づけるのが基本です。取引が紐づいた階層は削除できません(「無効」にしてください)。
        </p>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : nodes.length === 0 ? (
          <p className="empty">この事業部の階層はまだ登録されていません。「最上位を追加」から作成してください。</p>
        ) : (
          renderTree(null)
        )}
      </div>

      {edit && (
        <Modal onClose={() => setEdit(null)} width="min(560px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <h3>{edit.id ? '階層を編集' : '階層を追加'}</h3>
              <div className="path" style={{ margin: '2px 0 0' }}>
                親: {edit.parentName}
              </div>
            </div>
          </div>

          <div className="form-grid">
            <div>
              <label>名称</label>
              <input
                autoFocus
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                placeholder="例)港北"
              />
            </div>
            <div>
              <label>階層の名称(レベル)</label>
              <input
                value={edit.levelLabel}
                onChange={(e) => setEdit({ ...edit, levelLabel: e.target.value })}
                placeholder="例)事業所"
              />
            </div>
            <div>
              <label>表示順</label>
              <input
                type="number"
                value={edit.displayOrder}
                onChange={(e) => setEdit({ ...edit, displayOrder: Number(e.target.value) })}
              />
            </div>
            <div>
              <label>状態</label>
              <select
                value={edit.active ? '1' : '0'}
                onChange={(e) => setEdit({ ...edit, active: e.target.value === '1' })}
              >
                <option value="1">有効</option>
                <option value="0">無効</option>
              </select>
            </div>
          </div>

          <button className="btn-green" onClick={() => void save()} disabled={busy}>
            {busy ? '保存中…' : '保存'}
          </button>
          <p className="hint" style={{ marginTop: 10 }}>
            親の付け替えはできません(集計に使うパスを作り直す必要があるため)。
            移動が必要な場合は新しく作って古い方を無効にしてください。
          </p>
        </Modal>
      )}
    </>
  );
}
